import React, { FC, useCallback, useEffect, useRef, useState } from 'react'
import { Column } from 'react-table'
import { Box, Button, Checkbox, Container, DialogContent, FormControl, FormControlLabel, Grid, InputLabel, makeStyles, MenuItem, Select, TextField, Typography } from '@material-ui/core'
import { Autocomplete } from '@material-ui/lab'
import Table from '../../components/Table'
import FormDialog from "../../components/dialogs/FormDialog"
import NoDataDialog from '../../components/dialogs/ErrorDialog'
import DividerWithText from '../../components/layout/dividerwithtext'
import moment from "moment"
import "moment/locale/da"
import * as API from '../../api/customermanagement/index'
import * as Models from '../../api/customermanagement/models'
import * as CustomerManagementAPI from '../../api/customermanagement/index'
import * as UserManagementModels from '../../api/usermanagement/models'
import * as ShortModels from '../../api/short/models'
import APIRequest from '../../components/helpers/APIRequest'

moment.locale("da")

const useStyles = makeStyles((theme) => ({
    paper: {
        [theme.breakpoints.up('sm')]: {
            padding: '0',
        },
        marginTop: theme.spacing(3),
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
    },
    formControl: {
        margin: theme.spacing(1),
        minWidth: 120,
    },
    buts: {
        width: '100%',
        height: '100%'
    },
    grid: {
        width: '100%', // Fix IE 11 issue.
        marginTop: theme.spacing(3),
    },
    textHelp: {
        marginBottom: '1rem'
    },
    margTop: {
        marginTop: theme.spacing(2),
    }
}))

const Users: FC = () => {
    const classes = useStyles()

    const [customers, setCustomers] = useState<Models.CustomersResponse>(new Models.CustomersResponse())
    const [isPendingGetCustomers, setPendingGetCustomers] = useState(false)
    const [runGetCustomers, setRunGetCustomers] = useState(true)
    const [customerId, setCustomerId] = useState(0)

    const [showNoDataDialog, setShowNoDataDialog] = React.useState(false)
    const [showErrorDialog, setShowErrorDialog] = React.useState(0)

    const [users, setUsers] = useState<UserManagementModels.UsersData>(new UserManagementModels.UsersData())
    const [isPendingGetUsers, setPendingGetUsers] = useState(false)
    const [runGetUsers, setRunGetUsers] = useState(false)

    // Location data
    const [locationData, setLocationData] = useState<ShortModels.Location[]>([])
    const [sortedLocationData, setSortedLocationData] = useState<ShortModels.Location[]>([])
    const [isPendingLocationData, setIsPendingLocationData] = useState(false)
    const [runRequestLocationData, setRunRequestLocationData] = useState(false)

    const [showOnlyActiveUsers, setShowOnlyActiveUsers] = useState(true)

    // Dialog and related API control
    const [runRequestStoreUser, setRunRequestStoreUser] = useState(false)
    const [isPendingRequestStoreUser, setPendingRunRequestStoreUser] = useState(false)
    const [editUserOpen, setEditUserOpen] = useState(false)
    const [editDialogTitle, setEditDialogTitle] = useState("")

    // Definitions for the edit dialog
    const [userId, setUserId] = useState<number>(0)
    const [userUsername, setUserUsername] = useState("")
    const [userName, setUserName] = useState("")
    const [userEmail, setUserEmail] = useState("")
    const [userTitle, setUserTitle] = useState("")
    const [userPhone, setUserPhone] = useState("")
    const [userPassword, setUserPassword] = useState("")
    const [userActive, setUserActive] = useState(false)
    const [userPermissionAlarms, setUserPermissionAlarms] = useState(false)
    const [userPermissionDoorOpener, setUserPermissionDoorOpener] = useState(false)
    const [userPermissionGlobalSearch, setUserPermissionGlobalSearch] = useState(false)
    const [userPermissionInfoModule, setUserPermissionInfoModule] = useState(false)
    const [userPermissionMobileBase, setUserPermissionMobileBase] = useState(false)
    const [userPermissionStatistics, setUserPermissionStatistics] = useState(false)
    const [userPermissionUserManagement, setUserPermissionUserManagement] = useState(false)
    const [userPermissionDigitalLicenses, setUserPermissionDigitalLicenses] = useState(false)
    const [userLocations, setUserLocations] = useState<ShortModels.Location[]>([])

    // Necessary for avoiding strikethrough on the location combobox
    const labelRef = useRef(null)
    // @ts-ignore: Object is possibly 'null'
    const labelWidth = (labelRef !== null && labelRef.current !== null) ? labelRef.current.clientWidth : 0

    useEffect(() => {
        if (customers.result.length > 0) {
            setCustomerId(customers.result[0].id)
        }
    }, [customers, setCustomerId])

    useEffect(() => {
        if (customerId !== 0) {
            setRunGetUsers(true)
            setRunRequestLocationData(true)
        }
        setUsers(new UserManagementModels.UsersData())
    }, [customerId, setRunGetUsers, setUsers])

    useEffect(() => {
        if (!locationData || locationData.length < 1) {
            setSortedLocationData([])
        }

        let data: ShortModels.Location[] = [{id: -1, name: "Alle"}]
        data = data.concat(locationData.sort((a, b) => { return a.name.localeCompare(b.name)}))
        setSortedLocationData(data)
    }, [locationData])

    const renderPermissionsCell = (user: UserManagementModels.User) => {
        let u: string[] = []
        if (user.permissionAlarms) { u.push("alarmer") }
        if (user.permissionDoorOpener) { u.push("SMS døråbner") }
        if (user.permissionGlobalSearch) { u.push("parkant opslag") }
        if (user.permissionInfoModule) { u.push("infomodul") }
        if (user.permissionMobileBase) { u.push("mobilbase") }
        if (user.permissionStatistics) { u.push("statistik") }
        if (user.permissionUserManagement) { u.push("brugerhåndtering") }
        if (user.permissionDigitalLicenses) { u.push("digitale licenser") }
        var s = u.join(", ")
        if (s.length > 0) {
            return s.charAt(0).toUpperCase() + s.slice(1)
        }
        // No permissions
        return "-"
    }

    const columns: Column<UserManagementModels.User>[] = React.useMemo(() => [
        { Header: 'ID', id: "0", accessor: "id" }, 
        { Header: 'Brugernavn', id: "1", accessor: 'username'}, 
        { Header: 'Navn', id: "2", accessor: 'name' }, 
        { Header: 'Email', id: "3", accessor: 'email' }, 
        { Header: 'Titel', id: "4", accessor: 'title' },
        { Header: 'Mobiltelefon', id: "5", accessor: 'phone' }, 
        { Header: 'Aktiv', id: "6", accessor: d =>d.active ? "Ja" : "Nej" }, 
        { Header: 'Rettigheder', id: "7", accessor: user => renderPermissionsCell(user)}
    ], [])

    const sortAndSetUserLocations = (locations: ShortModels.Location[]) => {
        if (locations.length === 1 && locations[0].id === -1) {
            // User clicked the all button with nothing selected
            locations = locationData
        } else if (locations.find(l => l.id === -1)) {
            // The all button was pressed
            if (locations.length < sortedLocationData.length) {
                // Not all locations were previously checked, choose all
                locations = locationData
            } else {
                // Everything was checked, so now we should clear it
                locations = []
            }
        }
        locations.sort((a, b) => a.name.localeCompare(b.name))
        setUserLocations(locations)
    }

    const handleClickNewUser = () => {
        // Run the dialog with a new user
        handleRowClick(new UserManagementModels.User())
    }

    const handleRowClick = (element: UserManagementModels.User) => {
        if (element.id === 0) {
            setEditDialogTitle("Ny bruger")
        } else {
            setEditDialogTitle("Ændring af bruger")
        }

        setUserId(element.id)
        setUserUsername(element.username)
        setUserName(element.name.trim())
        setUserEmail(element.email)
        setUserTitle(element.title)
        setUserPhone(element.phone)
        setUserPassword("") // The password should always be empty, even for editing current users. Will be set if it's non-empty
        setUserActive(element.active)
        setUserPermissionAlarms(element.permissionAlarms)
        setUserPermissionDoorOpener(element.permissionDoorOpener)
        setUserPermissionGlobalSearch(element.permissionGlobalSearch)
        setUserPermissionInfoModule(element.permissionInfoModule)
        setUserPermissionMobileBase(element.permissionMobileBase)
        setUserPermissionStatistics(element.permissionStatistics)
        setUserPermissionUserManagement(element.permissionUserManagement)
        setUserPermissionDigitalLicenses(element.permissionDigitalLicenses)

        // Set the locations list
        let locations: ShortModels.Location[] = []
        element.locationsAccess.forEach(location => {
            var ld = locationData.find(l => l.id === location)
            if (ld) {
                locations.push(ld)
            }
        })
        sortAndSetUserLocations(locations)

        setEditUserOpen(true)
    }

    const RequestGetUsers = () => {
        return (<APIRequest<UserManagementModels.UsersData>
            run={runGetUsers} setRun={setRunGetUsers}
            pending={isPendingGetUsers} setIsPending={setPendingGetUsers}
            req={() => CustomerManagementAPI.getCustomerUsers(customerId)}
            setData={setUsers}
            />
        )
    }

    const RequestStoreUser = () => {
        const userParam = () => {
            return new UserManagementModels.StoreParams(userUsername ? userUsername.trim() : "", userName ? userName.trim() : "", userEmail ? userEmail.trim() : "",
                userTitle ? userTitle.trim() : "", userPhone ? userPhone.trim() : "", userPassword ? userPassword.trim() : "", userActive, userPermissionAlarms, userPermissionDoorOpener,
                userPermissionGlobalSearch, userPermissionInfoModule, userPermissionMobileBase, userPermissionStatistics, userPermissionUserManagement, userPermissionDigitalLicenses,
                userLocations.map(l => l.id))
        }
        return (<APIRequest<UserManagementModels.UsersData>
            run={runRequestStoreUser} setRun={setRunRequestStoreUser}
            pending={isPendingRequestStoreUser} setIsPending={setPendingRunRequestStoreUser}
            setData={setUsers}
            req={() => CustomerManagementAPI.storeCustomerUser(customerId, userId, userParam())}
            setShowError={setShowNoDataDialog}
        />)
    }

    const RequestGetCustomers = () => {
        return (<APIRequest<Models.CustomersResponse>
            run={runGetCustomers} setRun={setRunGetCustomers}
            pending={isPendingGetCustomers} setIsPending={setPendingGetCustomers}
            req={() => API.getCustomers()}
            setData={setCustomers}
            setShowError={setShowNoDataDialog}
        />)
    }

    const RequestLocationData = () => {
        return (<APIRequest<ShortModels.Location[]>
            run={runRequestLocationData} setRun={setRunRequestLocationData}
            pending={isPendingLocationData} setIsPending={setIsPendingLocationData}
            req={() => API.getCustomerLocations(customerId)}
            setData={setLocationData}
        />)
    }

    const activeFilter = useCallback((users: UserManagementModels.User[]) => {
        if (showOnlyActiveUsers) {
            return users.filter((user) => user.active)
        }
        return users
    }, [showOnlyActiveUsers])

    // Used in the dialog to update the list of locations
    const setLocationSelection = (selection: ShortModels.Location[] | null) => {
        sortAndSetUserLocations(selection ? selection : [])
    }

    const submitStoreUser = () => {
        if (userUsername.trim().length === 0) {
            setShowErrorDialog(-1)
        } else if (userName.trim().length === 0) {
            setShowErrorDialog(-2)
        } else if (userId === 0 && userEmail.trim().length === 0) {
            setShowErrorDialog(-3)
        } else {
            setRunRequestStoreUser(true)
            setEditUserOpen(false)
        }
    }

    const errorMessage = () => {
        switch (showErrorDialog) {
            case -1: return "Brugernavn må ikke være tomt"
            case -2: return "Navn må ikke være tomt"
            case -3: return "Email må ikke være tom for nye brugere"

            case 406: return "Ukendt fejl, kunne ikke gemme oplysningerne. Prøv igen senere."
            case 409: return "Dette brugernavn er optaget, prøv et andet."

            case 0: return "" // No error
        }
        return "Der er sket en fejl. Prøv igen senere."
    }

    return (
        <Container maxWidth={false}>
            <RequestGetUsers />
            <RequestStoreUser />
            <RequestGetCustomers />
            <RequestLocationData />
            <div className={classes.paper}>
                <Typography component="h1" variant="h5">Brugere</Typography>
                <Container maxWidth={"lg"} className={classes.margTop}>
                <Grid container spacing={2}>
                    <Grid item sm={4}>
                        <FormControl variant="outlined" fullWidth>
                            <InputLabel ref={labelRef} shrink htmlFor="ps-customerslist">Kunde</InputLabel>
                            <Select labelWidth={labelWidth} variant="outlined" id="ps-customerslist" value={customerId} onChange={(event: React.ChangeEvent<{ value: unknown }>) => {setCustomerId(event.target.value as number)}}>
                                {customers.result.filter((c) => {return c.active}).sort((a, b) => a.navn.localeCompare(b.navn)).map((customer) => {
                                    return <MenuItem key={`CustomerId-${customer.id}`} value={customer.id}>{customer.navn}</MenuItem>
                                })}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item sm={4}>
                        <Button variant="contained" color="primary" className={classes.buts} onClick={handleClickNewUser}>Tilføj bruger</Button>
                    </Grid>
                    <Grid item sm={4}>
                        <FormControlLabel control={<Checkbox name="activeUsers" id="activeUsers" checked={showOnlyActiveUsers} onChange={(e: React.ChangeEvent<HTMLInputElement>) => {setShowOnlyActiveUsers(e.currentTarget.checked)}} />} label="Vis kun aktive brugere" />
                    </Grid>
                </Grid>
                <Box mt={2}>
                    <Table<UserManagementModels.User> columns={columns} data={activeFilter(users.result.users)} initialState={{pageSize: 150}} clickEvent={handleRowClick} update={null} pending={false} text={""}/>
                </Box>
                </Container>
            </div>

            <NoDataDialog open={showNoDataDialog} onClose={() => {setShowNoDataDialog(false)}} title={"Intet svar"} text={"Noget gik galt, prøv venligst igen senere."}/>

            <NoDataDialog open={showErrorDialog !== 0} onClose={() => {setShowErrorDialog(0); setEditUserOpen(true)}} title={"Fejl"} text={errorMessage()}/>

            <FormDialog title={editDialogTitle} open={editUserOpen} onCloseCancel={() => setEditUserOpen(false)} onCloseSubmit={submitStoreUser}>
                <DialogContent>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField autoComplete="username" name="username" variant="outlined" fullWidth id="username" label="Brugernavn" value={userUsername} autoFocus onChange={(e: React.ChangeEvent<HTMLInputElement>) => {setUserUsername(e.currentTarget.value);}} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField autoComplete="name" name="name" variant="outlined" fullWidth id="name" label="Navn" value={userName} autoFocus onChange={(e: React.ChangeEvent<HTMLInputElement>) => {setUserName(e.currentTarget.value);}} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField autoComplete="email" name="email" variant="outlined" fullWidth id="email" label="Email" value={userEmail} onChange={(e: React.ChangeEvent<HTMLInputElement>) => {setUserEmail(e.currentTarget.value);}} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField autoComplete="title" name="title" variant="outlined" fullWidth id="title" label="Titel" value={userTitle} onChange={(e: React.ChangeEvent<HTMLInputElement>) => {setUserTitle(e.currentTarget.value);}} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField autoComplete="phone" name="phone" variant="outlined" fullWidth id="phone" label="Telefon" value={userPhone} onChange={(e: React.ChangeEvent<HTMLInputElement>) => {setUserPhone(e.currentTarget.value);}} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField autoComplete="password" type="password" name="password" variant="outlined" fullWidth id="password" label="Password" value={userPassword} onChange={(e: React.ChangeEvent<HTMLInputElement>) => {setUserPassword(e.currentTarget.value);}} />
                        </Grid>
                        <Grid item xs={12} sm={12}>
                            <FormControlLabel
                                control={<Checkbox name="active" id="active" checked={userActive} onChange={(e: React.ChangeEvent<HTMLInputElement>) => {setUserActive(e.currentTarget.checked)}} />}
                                label="Aktiv" />
                            <DividerWithText>Rettigheder</DividerWithText>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControlLabel
                                control={<Checkbox name="permission_alarms" id="permission_alarms" label="Alarmer" checked={userPermissionAlarms} onChange={(e: React.ChangeEvent<HTMLInputElement>) => {setUserPermissionAlarms(e.currentTarget.checked)}} />}
                                label="Alarmer" />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControlLabel
                                control={<Checkbox disabled={!users.result.permissions.doorOpener} name="permission_dooropener" id="permission_dooropener" label="SMS døråbner" checked={userPermissionDoorOpener} onChange={(e: React.ChangeEvent<HTMLInputElement>) => {setUserPermissionDoorOpener(e.currentTarget.checked);}} />}
                                label="SMS døråbner" />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControlLabel
                                control={<Checkbox disabled={!users.result.permissions.globalSearch} name="permission_globalSearch" id="permission_globalSearch" label="Parkantopslag" checked={userPermissionGlobalSearch} onChange={(e: React.ChangeEvent<HTMLInputElement>) => {setUserPermissionGlobalSearch(e.currentTarget.checked)}} />}
                                label="Parkantopslag" />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControlLabel
                                control={<Checkbox disabled={!users.result.permissions.infoModule} name="permission_infomodul" id="permission_infomodul" label="Infomodul" checked={userPermissionInfoModule} onChange={(e: React.ChangeEvent<HTMLInputElement>) => {setUserPermissionInfoModule(e.currentTarget.checked)}} />}
                                label="Infomodul" />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControlLabel
                                control={<Checkbox name="permission_mobilbase" id="permission_mobilbase" label="Mobilbase" checked={userPermissionMobileBase} onChange={(e: React.ChangeEvent<HTMLInputElement>) => {setUserPermissionMobileBase(e.currentTarget.checked);}} />}
                                label="Mobilbase" />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControlLabel
                                control={<Checkbox name="permission_stats" id="permission_stats" label="Statistik" checked={userPermissionStatistics} onChange={(e: React.ChangeEvent<HTMLInputElement>) => {setUserPermissionStatistics(e.currentTarget.checked);}} />}
                                label="Statistik" />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControlLabel
                                control={<Checkbox name="permission_users" id="permission_users" label="Brugerhåndtering" checked={userPermissionUserManagement} onChange={(e: React.ChangeEvent<HTMLInputElement>) => {setUserPermissionUserManagement(e.currentTarget.checked);}} />}
                                label="Brugerhåndtering" />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControlLabel
                                control={<Checkbox disabled={!users.result.permissions.digitalLicenses} name="permission_digital_licenses" id="permission_digital_licenses" label="Digitale licenser" checked={userPermissionDigitalLicenses} onChange={(e: React.ChangeEvent<HTMLInputElement>) => {setUserPermissionDigitalLicenses(e.currentTarget.checked);}} />}
                                label="Digitale licenser" />
                        </Grid>
                        <Grid item xs={12} sm={12}>
                            <Autocomplete multiple value={userLocations} options={sortedLocationData} getOptionLabel={option => option.name}
                                renderInput={params => (<TextField {...params} label="Pladsadgang" variant="outlined" fullWidth />)}
                                onChange={(event: any, newSelection: ShortModels.Location[] | null) => setLocationSelection(newSelection)}
                                renderOption={(option, state) => {
                                    return (<React.Fragment>
                                        <Checkbox indeterminate={option.id === -1 && userLocations.length > 0 && userLocations.length < locationData.length}
                                            checked={(option.id !== -1 && state.selected) || (option.id === -1 && userLocations.length > 0 && userLocations.length === locationData.length)}/>
                                            {option.name}
                                    </React.Fragment>) }}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
            </FormDialog>
        </Container>
    )
}

export default Users
